// PATH: src/utils/session.js   (NEW FILE)
// Single source of truth for "is this user really logged in?".
// Every guard / provider / axios interceptor uses these helpers so that
// they can never disagree with each other.
import Cookies from "js-cookie";
import { isTokenValid, getTokenExpiry } from "utils/jwt";

export const AUTH_EXPIRED_EVENT = "auth:expired";

// Every cookie that belongs to a login session.
export const SESSION_COOKIE_KEYS = [
  "access_token",
  "name",
  "email",
  "userid",
  "profileImg",
  "role",
  "roleid",
  "rolename",
];

const MAX_SESSION_DAYS = 15;

// js-cookie writes the literal string "undefined" when you pass undefined,
// and "undefined" is truthy -> this helper rejects those fake values.
export const isFilled = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value !== "undefined" &&
  value !== "null";

// All cookies get the SAME lifetime as the JWT (max 15 days), so access_token
// can never outlive email / userid again.
export const getCookieOptions = (token) => {
  const maxDate = new Date(Date.now() + MAX_SESSION_DAYS * 24 * 60 * 60 * 1000);
  const tokenExpiry = getTokenExpiry(token);
  const expires = tokenExpiry && tokenExpiry < maxDate ? tokenExpiry : maxDate;

  return {
    path: "/",
    sameSite: "Lax",
    secure: window.location.protocol === "https:",
    expires,
  };
};

export const writeSessionCookies = ({
  token,
  name,
  email,
  userid,
  profileImg,
  role,
  roleid,
  rolename,
}) => {
  const opts = getCookieOptions(token);
  Cookies.set("access_token", token, opts);
  Cookies.set("name", name || "", opts);
  Cookies.set("email", email, opts);
  Cookies.set("userid", String(userid), opts);
  Cookies.set("profileImg", profileImg || "", opts);
  Cookies.set("role", role || "", opts);
  Cookies.set("roleid", roleid === null || roleid === undefined ? "" : String(roleid), opts);
  Cookies.set("rolename", rolename || "", opts);
};

export const clearSessionCookies = () => {
  SESSION_COOKIE_KEYS.forEach((key) => Cookies.remove(key, { path: "/" }));
  try {
    localStorage.removeItem("authToken");
  } catch {
    /* storage blocked - ignore */
  }
};

export const hasAnySessionCookie = () =>
  SESSION_COOKIE_KEYS.some((key) => Cookies.get(key) !== undefined);

// A session is valid ONLY when ALL of these are true:
//   1. access_token cookie exists and is a real JWT
//   2. JWT is not expired
//   3. email cookie exists (not "undefined")
//   4. userid cookie exists (not "undefined")
export const getSessionStatus = () => {
  const token = Cookies.get("access_token");

  if (!isFilled(token)) return { valid: false, reason: "no_token", token: null };
  if (!isTokenValid(token)) return { valid: false, reason: "token_expired_or_invalid", token: null };
  if (!isFilled(Cookies.get("email"))) return { valid: false, reason: "no_email", token: null };
  if (!isFilled(Cookies.get("userid"))) return { valid: false, reason: "no_userid", token: null };

  return { valid: true, reason: null, token };
};

export const hasValidSession = () => getSessionStatus().valid;

// ---------------------------------------------------------------------------
// LOGOUT HELPERS
// ---------------------------------------------------------------------------

// UI preferences only (theme / language). Everything else is wiped on logout.
// Make this array empty if you want a TOTAL localStorage wipe.
const PRESERVE_LOCAL_KEYS = [
  "settings", // theme / layout settings
  "i18nextLng", // language
  "RAAJNEETI_LANDING_THEME", // landing page theme
  "RAAJNEETI_NAVY_DEFAULT_V1", // navy default already applied
  "raajneeti_layout_migrated_v1", // layout migration flag
];

// Wipes EVERYTHING that can identify a session: every cookie (not only ours,
// also with domain variants), localStorage (except UI prefs above) and the
// whole sessionStorage.
export const clearAllClientData = () => {
  // 1) every cookie JS can see
  try {
    const host = window.location.hostname;
    const domains = [undefined, host, `.${host}`];
    const names = new Set([...Object.keys(Cookies.get()), ...SESSION_COOKIE_KEYS]);

    names.forEach((name) => {
      domains.forEach((domain) => {
        Cookies.remove(name, domain ? { path: "/", domain } : { path: "/" });
      });
    });
  } catch {
    /* ignore */
  }

  // 2) localStorage (keep only UI preferences)
  try {
    Object.keys(localStorage).forEach((key) => {
      if (!PRESERVE_LOCAL_KEYS.includes(key)) localStorage.removeItem(key);
    });
  } catch {
    /* storage blocked - ignore */
  }

  // 3) sessionStorage - everything
  try {
    sessionStorage.clear();
  } catch {
    /* storage blocked - ignore */
  }
};

// After a MANUAL logout the user is sent to the home page and the login modal
// must NOT pop up (AuthGuard normally opens it for logged-out visitors).
// In-memory on purpose: a page reload resets it.
let manualLogoutAt = 0;
export const markManualLogout = () => {
  manualLogoutAt = Date.now();
};
export const isManualLogoutRecent = () => Date.now() - manualLogoutAt < 5000;